/**
 * Brand-Kontext (White-Label): laedt `GET /api/public/brand` (Deployment-
 * Default-Marke — die Operator-Konsole laeuft auf dem bare Host, nicht unter
 * einem Tenant) und wendet Titel, Favicon und `--sec-*`-Farb-Tokens an.
 *
 * Bis die Antwort da ist, gelten die Haus-Defaults; schlaegt der Request
 * fehl, bleiben sie. Ein Haus-Deployment rendert damit exakt wie bisher
 * (keine Token-Overrides, Original-Favicon, "SecTepe Platform Admin").
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  HOUSE_BRAND,
  HOUSE_DOMAIN,
  applyBrandToDocument,
  fetchPublicBrand,
  type BrandConfig,
  type BrandSource,
} from '../services/brand';

export interface BrandCtx {
  brand: BrandConfig;
  /** Woher die Marke stammt; `null` solange noch nicht geladen. */
  source: BrandSource | null;
  /** true sobald der Public-Endpunkt geantwortet hat (auch bei Fallback). */
  loaded: boolean;
  /**
   * Basis-Domain der Plattform (z. B. `partner.example`), unter der die
   * Produkt-Hostnamen `portal-<slug>.<domain>` usw. laufen. Kommt aus
   * `GET /api/public/brand`; bis dahin bzw. bei Fehler die Haus-Domain.
   */
  domain: string;
}

const Ctx = createContext<BrandCtx>({ brand: HOUSE_BRAND, source: null, loaded: false, domain: HOUSE_DOMAIN });

/** Dokument-Titel der Operator-Konsole fuer eine Marke. */
export function documentTitleFor(brand: Pick<BrandConfig, 'name'>): string {
  return `${brand.name} Platform Admin`;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BrandCtx>({ brand: HOUSE_BRAND, source: null, loaded: false, domain: HOUSE_DOMAIN });

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPublicBrand({ signal: ctrl.signal }).then((r) => {
      if (ctrl.signal.aborted) return;
      setState({ brand: r.brand, source: r.source, loaded: true, domain: r.domain });
    });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    document.title = documentTitleFor(state.brand);
    applyBrandToDocument(state.brand);
  }, [state.brand]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export const useBrand = () => useContext(Ctx);
